import maplibregl, { type Map as MapLibreMap, type Marker } from "maplibre-gl";
import { isLngLat } from "../../shared/geo.ts";
import type { PlaceSearchHit } from "../../shared/place-search.ts";
import type { LngLat } from "../../shared/types.ts";
import { VECTOR_STYLE_URL } from "../../shared/basemap.ts";
import { searchPlaces } from "../api.ts";
import { locale, t } from "../i18n/index.ts";
import { MyLocationControl } from "../home/my-location-control.ts";
import { button, el } from "./dom.ts";
import { whenMapHasStyle } from "../../shared/map-style-safe.ts";

export interface SpotLocationPickerOpts {
  container: HTMLElement;
  initial: LngLat;
  onConfirm: (point: LngLat, address: string | null) => void;
  onToast: (message: string) => void;
}

/** Inline map + search for choosing a spot coordinate (draft editor). */
export class SpotLocationPicker {
  private map: MapLibreMap;
  private marker: Marker;
  private draft: LngLat;
  private draftAddress: string | null = null;
  private searchTimer = 0;
  private searchSeq = 0;
  private locating = false;
  private destroyed = false;
  private resultsEl: HTMLElement;
  private previewEl: HTMLElement;
  private searchInput: HTMLInputElement;
  private searchWrap: HTMLElement;
  private myLocation: MyLocationControl;
  private readonly opts: SpotLocationPickerOpts;
  private resizeObserver: ResizeObserver;

  constructor(opts: SpotLocationPickerOpts) {
    this.opts = opts;
    this.draft = opts.initial;
    const mapEl = el("div", "rt-spot-loc-map");
    opts.container.replaceChildren();
    const searchRow = el("div", "rt-spot-loc-search");
    this.searchWrap = el("div", "rt-spot-loc-input-wrap");
    this.searchInput = el("input", "rt-text rt-spot-loc-input") as HTMLInputElement;
    this.searchInput.type = "search";
    this.searchInput.placeholder = t("rt.spot.location.search");
    this.searchInput.autocomplete = "off";
    this.searchWrap.append(this.searchInput);
    this.resultsEl = el("div", "rt-spot-loc-results");
    searchRow.append(this.searchWrap, this.resultsEl);
    this.previewEl = el("p", "rt-spot-loc-preview");
    const actions = el("div", "rt-spot-loc-actions");
    const confirm = button(t("rt.spot.location.set"), "rt-next", () => {
      this.opts.onConfirm(this.draft, this.draftAddress);
    });
    opts.container.append(searchRow, mapEl, this.previewEl, actions);
    actions.append(confirm);
    this.map = new maplibregl.Map({
      container: mapEl,
      style: VECTOR_STYLE_URL,
      center: this.draft,
      zoom: 15,
      attributionControl: false,
    });
    this.marker = new maplibregl.Marker({ draggable: true, color: "#8b5cf6" })
      .setLngLat(this.draft)
      .addTo(this.map);
    this.marker.on("dragend", () => {
      const lngLat = this.marker.getLngLat();
      this.setDraft([lngLat.lng, lngLat.lat], null);
    });
    this.myLocation = new MyLocationControl(() => this.useMyLocation());
    this.map.addControl(this.myLocation, "bottom-right");
    this.map.on("click", (e) => this.setDraft([e.lngLat.lng, e.lngLat.lat], null));
    this.searchInput.addEventListener("input", () => {
      window.clearTimeout(this.searchTimer);
      const q = this.searchInput.value.trim();
      if (q.length < 2) {
        this.setSearchBusy(false);
        this.resultsEl.replaceChildren();
        return;
      }
      this.showSearching();
      this.searchTimer = window.setTimeout(() => void this.runSearch(q), 320);
    });
    whenMapHasStyle(this.map, () => this.paintPreview());
    this.paintPreview();
    this.resizeObserver = new ResizeObserver(() => {
      if (this.destroyed) return;
      this.map.resize();
    });
    this.resizeObserver.observe(mapEl);
    requestAnimationFrame(() => this.map.resize());
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    window.clearTimeout(this.searchTimer);
    this.resizeObserver.disconnect();
    this.marker.remove();
    this.map.remove();
  }

  private setSearchBusy(busy: boolean): void {
    this.searchWrap.classList.toggle("is-searching", busy);
    this.searchInput.setAttribute("aria-busy", busy ? "true" : "false");
  }

  private setDraft(point: LngLat, address: string | null): void {
    if (!isLngLat(point)) return;
    this.draft = point;
    this.draftAddress = address;
    this.marker.setLngLat(point);
    this.map.easeTo({ center: point, duration: 350 });
    this.paintPreview();
  }

  private paintPreview(): void {
    const [lng, lat] = this.draft;
    const addr = this.draftAddress?.trim();
    this.previewEl.textContent = addr
      ? t("rt.spot.location.preview", { lat: lat.toFixed(5), lng: lng.toFixed(5), address: addr })
      : t("rt.spot.location.coords", { lat: lat.toFixed(5), lng: lng.toFixed(5) });
  }

  private showSearching(): void {
    this.setSearchBusy(true);
    this.resultsEl.replaceChildren(el("p", "rt-spot-loc-status", t("rt.spot.location.searching")));
  }

  private async runSearch(query: string): Promise<void> {
    if (this.destroyed) return;
    const seq = ++this.searchSeq;
    this.showSearching();
    try {
      const { results } = await searchPlaces(query, locale());
      if (this.destroyed || seq !== this.searchSeq) return;
      this.setSearchBusy(false);
      this.renderResults(results);
    } catch (err) {
      if (this.destroyed || seq !== this.searchSeq) return;
      this.setSearchBusy(false);
      const msg = err instanceof Error ? err.message : t("rt.spot.location.searchFailed");
      this.resultsEl.replaceChildren(el("p", "rt-spot-loc-error", msg));
    }
  }

  private renderResults(hits: PlaceSearchHit[]): void {
    this.resultsEl.replaceChildren();
    if (!hits.length) {
      this.resultsEl.append(el("p", "rt-spot-loc-empty", t("rt.spot.location.noResults")));
      return;
    }
    for (const hit of hits) {
      const row = button("", "rt-spot-loc-hit", () => {
        this.setDraft([hit.lng, hit.lat], hit.address ?? hit.name);
        this.resultsEl.replaceChildren();
      });
      row.append(
        el("span", "rt-spot-loc-hit-name", hit.name),
        el("span", "rt-spot-loc-hit-addr", hit.address ?? ""),
      );
      this.resultsEl.append(row);
    }
  }

  private useMyLocation(): void {
    if (this.locating) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      this.opts.onToast(t("toast.geolocationUnavailable"));
      return;
    }
    this.locating = true;
    this.myLocation.setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.locating = false;
        this.myLocation.setBusy(false);
        this.setDraft([pos.coords.longitude, pos.coords.latitude], null);
      },
      (err) => {
        this.locating = false;
        this.myLocation.setBusy(false);
        this.opts.onToast(t(err.code === 1 ? "toast.geolocationDenied" : "toast.geolocationUnavailable"));
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 30_000 },
    );
  }
}
